import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button, Separator } from '@/components/ui'
import { useSEO, createArticleSchema } from '@/hooks/useSEO'
import { useScrollReveal, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { BeforeAfterSlider } from '@/components/BeforeAfterSlider'

const TITLE = 'Sacred Music by Women Composers, Now Searchable by the Sunday It Serves'
/** Kept under 160 characters so the payoff survives Google's truncation. */
const DESCRIPTION =
  'Three years of curation lived in a 17-tab spreadsheet no search engine could read. Now 842 sacred works by women composers are searchable by the church year.'

export default function GreatHostComposersCaseStudyPage() {
  const breadcrumbs = generateBreadcrumbs('/blog/great-host-composers-case-study', breadcrumbLabels)

  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const sectionsRef = useScrollReveal<HTMLElement>('.article-section', { stagger: 0.1 })

  useSEO({
    title: TITLE,
    description: DESCRIPTION,
    url: '/blog/great-host-composers-case-study',
    canonical: '/blog/great-host-composers-case-study',
    type: 'article',
    // Post-specific share card (the client's actual homepage at 1200x630).
    // NOTE: this line only updates the DOM at runtime, and social crawlers do
    // not run JS — the tag a scraper reads is baked at prerender time from the
    // matching `image` in seo.json. Both must be set, and must agree.
    image: 'https://images.fuguelab.app/media/case-studies/great-host-composers/blog/og.jpg',
    breadcrumbs,
    article: {
      publishedTime: '2026-08-06',
      author: 'Garrett John Law',
      section: 'Case Studies',
      tags: [
        'women composers',
        'sacred music',
        'church music',
        'lectionary',
        'website rebuild',
        'weebly',
        'search visibility',
      ],
    },
    schema: createArticleSchema({
      headline: TITLE,
      description: DESCRIPTION,
      datePublished: '2026-08-06',
      url: '/blog/great-host-composers-case-study',
    }),
  })

  return (
    <article ref={sectionsRef} className="mx-auto max-w-3xl space-y-12 px-4 py-16">
      {/* Header */}
      <header ref={heroRef} className="space-y-4">
        <Link to="/blog" className="text-primary text-sm hover:underline">
          &larr; Back to Blog
        </Link>
        <h1 className="text-fluid-h2">{TITLE}</h1>
        <div className="text-muted-foreground flex items-center gap-4 text-sm">
          <time dateTime="2026-08-06">August 6, 2026</time>
          <span>&middot;</span>
          <span>11 min read</span>
        </div>
        <p className="text-fluid-h4 text-muted-foreground">
          A Great Host of Composers had done the hard part. Three years, a grant from the Association of Anglican
          Musicians, and a team of nine had produced the best curated list of sacred choral and organ music by women
          composers I have seen. It lived in a Google Sheet with 17 tabs, and a search engine could not read a word of
          it.
        </p>
      </header>

      {/* Cover: drag-to-compare hero. The database pair, not the homepage pair:
          this comparison is the article's thesis. */}
      <figure className="not-prose">
        <BeforeAfterSlider
          beforeSrc="https://images.fuguelab.app/media/case-studies/great-host-composers/blog/before-database.webp"
          afterSrc="https://images.fuguelab.app/media/case-studies/great-host-composers/blog/after-music.webp"
          beforeAlt="The old database page, offering two ways to reach the music: a screenshot of the Google Sheet above a button reading Open A Great Host of Women Composers Spreadsheet, and beside it a scanned PDF listing for Year A"
          afterAlt="The rebuilt database page: a color-banded bar of the church year, then Advent and Christmas as sections, each liturgical day carrying a season-colored dot and the number of works available for it"
          beforeLabel="Spreadsheet and PDF"
          afterLabel="The church year"
          initialPos={45}
          width={1366}
          height={640}
        />
        <figcaption className="text-muted-foreground mt-3 text-sm">
          Drag the handle to compare. Both are the way into the same 842 pieces of music. On the left you leave the
          website for a spreadsheet or a PDF. On the right you are already inside it, and every day of the church year
          carries the number of works waiting on it.
        </figcaption>
      </figure>

      <Separator />

      {/* The problem */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">It Lived in a Google Sheet Behind a TinyURL</h2>
        <p>
          A Great Host of Composers is a faith-based initiative founded by Susan Jane Matthews, Director of Music at St.
          Paul&rsquo;s Episcopal Church in Burlingame, California. The mission is narrow: bring the music of women
          composers into sacred liturgies, by giving church musicians the repertoire and the recordings. The name comes
          from Psalm 68:11b, <em>a great host of women bring the glad tidings</em>, and the &ldquo;b&rdquo; is
          load-bearing, because the second half of the verse is the half about the women.
        </p>
        <p>
          Most of the collection is keyed to the three-year Revised Common Lectionary, Sunday by Sunday and feast by
          feast. The rest is the music the lectionary does not govern: Evensong, Mass settings, the responses and
          Anglican chant, weddings, and the saints&rsquo; days that run on their own calendar. That mix is ordinary
          parish reality and it is the first thing a system built for this has to accommodate.
        </p>
        <p>
          They published it the way you publish a dataset when nobody tells you not to. The website ran on Weebly, six
          pages of it, and the database page offered two ways in: a link to the Google Sheet, and four PDF outlines. The
          link to the sheet was a TinyURL. When I traced it, the TinyURL redirected through a VigLink affiliate
          interstitial before it reached Google Docs, so a director clicking the primary call to action on a
          grant-funded church music resource passed through an ad network on the way.
        </p>
        <p>The sheet itself was 17 tabs, 22 columns, and roughly 1,270 rows. Search was Cmd+F.</p>
        <p>
          A Google Sheet behind a shortener is one URL with no readable content, and a PDF outline is close to it. Every
          piece in that file was invisible to search. A music director in another diocese, typing &ldquo;SATB Magnificat
          woman composer&rdquo; into Google at 9pm while planning Advent, would never reach this work. Three years of
          research and a grant had produced a resource that only found the people who already knew it existed.
        </p>
        <p>
          The obvious question is why it stayed that way. I assumed budget. Susan told me afterward that it was not.
        </p>

        <blockquote className="border-primary my-8 border-l-4 pl-6 text-lg italic">
          <p>
            &ldquo;We diligently searched and interviewed prospective website designers, and they all lacked the
            expertise and understanding you hold in liturgy. I just gave up, as I felt it would be a disaster.&rdquo;
          </p>
          <footer className="text-muted-foreground mt-2 text-sm not-italic">
            Susan Jane Matthews, on the years before the rebuild
          </footer>
        </blockquote>

        <p>
          That is a harder sentence to read than it looks, and I want to be careful not to flatter myself with it. She
          is not saying those designers were bad at their work. She is saying they could not tell which columns were
          load-bearing. Voicing, season, liturgical placement and lectionary year look like arbitrary metadata to anyone
          outside church music, and the failure mode is not an ugly website, it is a tidy one that has quietly
          normalised the distinctions the collection exists to preserve. She ran the search, took the meetings, and
          concluded that hiring the wrong person would cost her the dataset. She was right, so she kept the sheet and
          kept it current herself, by her own account for six years.
        </p>
      </section>

      <Separator />

      {/* What the user is actually doing */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">What I Am Actually Doing on a Tuesday in November</h2>
        <p>
          I play these services. On a Tuesday in November I am not browsing a catalogue of choral music. I am planning
          Advent 2 in Year B, and I need to know five things fast: what fits the appointed readings, what my choir can
          sing, whether it wants the organ or stands unaccompanied, whether I can hear it before I commit, and whether
          it is still in print and will reach me before the Sunday. That last one decides more programs than the first
          four, and it is the one a repertoire list usually cannot answer.
        </p>
        <p>
          A spreadsheet cannot answer any of it in one pass. It can hold every fact you need and still make you do the
          joining in your head: filter the Year B tab, scan for Advent 2, read across 22 columns, hold the voicing in
          memory, check the forces column, open a link in a new tab to hear it. It takes me about five minutes a Sunday,
          and I know the sheet.
        </p>
        <p>
          So the database is organized by the day, not by the title. For a festival the equivalent unit is a concert,
          for a school a student. The point is that your users arrive holding something, and the thing they arrive
          holding should be the front door.
        </p>
      </section>

      <Separator />

      {/* The build */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">The Church Year Became the Navigation</h2>
        <p>
          The database opens on the liturgical calendar: Advent through the Season after Pentecost, then the groups the
          lectionary does not reach, Evensong and Mass settings and weddings and the commemorations. Each season carries
          its own color and each day shows how many works are waiting on it. Everyone in the building reads those colors
          already, so the color carries structure instead of decoration, and it costs a visitor nothing to learn.
        </p>
        <p>
          Above the calendar sits the filter bar the sheet could never offer: year, forces, voicing, difficulty,
          accompaniment, language. A director looking for something their upper-voices ensemble can sing unaccompanied
          in Lent gets there in two taps, and every filtered view is its own link, so they can send it to their rector
          or their section leaders. Each liturgical day also links out to its readings at lectionarypage.net, which is
          the next click anyone planning a service is going to make.
        </p>
      </section>

      <Separator />

      {/* The SEO half */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">842 Works That Google Can Now Read</h2>
        <p>
          Every work is its own page now, and so is every composer, every poet, and every Sunday and feast of the church
          year. The sitemap carries <strong>1,312 pages</strong>. The old site had six, and one of them pointed at a
          spreadsheet.
        </p>
        <p>
          Which means the director in another diocese, typing &ldquo;SATB Magnificat woman composer&rdquo; at 9pm, now
          lands on the piece itself: who wrote it, whose words it sets, the voicing, the readings it was chosen for, how
          long it runs, who publishes it, and a recording she can play before she orders twenty copies. The publisher
          and the duration are the two fields she actually buys on, and they were sitting in columns of the sheet that
          Google never saw.
        </p>

        {/* Metrics */}
        <div className="my-8 grid gap-6 text-center md:grid-cols-3">
          <div className="bg-muted/10 border-border rounded-lg border p-4">
            <p className="text-primary text-2xl font-bold">6 &rarr; 1,312</p>
            <p className="text-muted-foreground text-sm">Pages a search engine can read</p>
          </div>
          <div className="bg-muted/10 border-border rounded-lg border p-4">
            <p className="text-primary text-2xl font-bold">842 works</p>
            <p className="text-muted-foreground text-sm">By 265 women composers, from Kassia to the living</p>
          </div>
          <div className="bg-muted/10 border-border rounded-lg border p-4">
            <p className="text-primary text-2xl font-bold">17 tabs &rarr; 1 church year</p>
            <p className="text-muted-foreground text-sm">One index instead of seventeen</p>
          </div>
        </div>
      </section>

      {/* Visual: the work page, the atomic unit of the whole rebuild */}
      <figure className="not-prose article-section space-y-2">
        <div className="border-border h-[28rem] overflow-y-auto rounded-lg border">
          <img
            src="https://images.fuguelab.app/media/case-studies/great-host-composers/blog/work-detail.webp"
            alt="A single work page for Melissa Dunphy's A new heart: text source Ezekiel 36:26, category, forces, voicing SAB, accompaniment, language, duration 2:42, difficulty, publisher Morning Star, links to the score and a recording, then a Suggested for list of seven liturgical days each naming the readings it reflects on"
            width={1060}
            height={910}
            loading="lazy"
            className="w-full"
          />
        </div>
        <figcaption className="text-muted-foreground text-sm">
          One work page, scroll it. Melissa Dunphy&rsquo;s setting of Ezekiel 36:26 is suggested for seven different
          days across all three lectionary years, and the readings underneath each one are different. This is the page
          that did not exist before, multiplied by 842.
        </figcaption>
      </figure>

      <Separator />

      {/* Composers and poets */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">Rosalie Bonighton Was a String in a Column</h2>
        <p>
          In a spreadsheet a composer is text that repeats down a column. Rosalie Bonighton appears eleven times and is
          eleven pieces of text. On the rebuilt site every name is a link to her own page: her dates, her country, her
          website where there is one, and her works grouped by the season they serve. The index runs from Kassia of
          Constantinople, writing in the ninth century, to composers who are alive and taking commissions.
        </p>
        <p>
          Then Susan asked for something the sheet had been carrying in its formatting. She had been bolding the text
          sources written by women, a convention that meant something to every human who opened the file and nothing at
          all to any machine that read it. That bolding became the <strong>Poets</strong> section, 60 pages of women
          whose words are being sung, and she has since told me it is the direction she is most drawn to. It sat in the
          data for three years and could not be seen until the data became a website.
        </p>
      </section>

      {/* Visual: composers index, cropped to the Florence Price portraits */}
      <figure className="not-prose article-section space-y-2">
        <img
          src="https://images.fuguelab.app/media/case-studies/great-host-composers/blog/composers.webp"
          alt="The top of the rebuilt composers index, with a search field and four children's drawn portraits of Florence Price in pencil and crayon, credited to Alice M., Lexie L. and Daisy Adams of the Choir School of St. Paul's Episcopal Church"
          width={1366}
          height={569}
          loading="lazy"
          className="border-border w-full rounded-lg border"
        />
        <figcaption className="text-muted-foreground text-sm">
          The four portraits of Florence Price at the top of the composers index were drawn by children of the Choir
          School at Susan&rsquo;s parish, for a musical they wrote about her. That is the mission working, and no
          spreadsheet has a place to put it.
        </figcaption>
      </figure>

      <Separator />

      {/* The domain knowledge */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">Most of These Decisions Were Liturgical, Not Technical</h2>
        <p>
          Moving 1,270 spreadsheet rows into a database is a morning&rsquo;s work. Moving them <em>correctly</em> took
          knowing what the rows meant. The rows are also not 1,270 pieces: an anthem gets a row for every day it serves,
          which collapses to 842 distinct works across about 1,150 work-and-day pairings, with the remainder being
          header rows and the contributor-initial legend that each tab repeats.
        </p>

        <h3 className="mt-8 text-xl font-semibold">A piece&rsquo;s readings belong to the pairing, not to the piece</h3>
        <p>
          Melissa Dunphy&rsquo;s <em>A new heart</em> sets one verse of Ezekiel and is suggested for seven days across
          all three lectionary years. On Proper 19 it answers Matthew&rsquo;s parable of the unforgiving servant; on
          Maundy Thursday it answers the new commandment in John 13; at the Easter Vigil it answers Ezekiel itself. Same
          anthem, different reason each time. Flatten those readings onto the work and you lose the reason anybody chose
          it, which is the only part a director is actually reading.
        </p>

        <h3 className="mt-8 text-xl font-semibold">Betty Jackson King files under King</h3>
        <p>
          Katie Burk and Katherine Burk Webb are one person. An alphabetical index that gets this wrong is worse than no
          index, because a director who cannot find her concludes she is not there. For a project whose whole purpose is
          to make these composers findable, a filing error is a mission failure wearing a typo.
        </p>

        <h3 className="mt-8 text-xl font-semibold">Some entries have no lectionary year at all</h3>
        <p>
          Advent Lessons and Carols does not use the Revised Common Lectionary. It follows its own scheme of lessons, so
          splitting it across Years A, B and C would have made three copies of one service and hidden two of them from
          any given visitor. Christmas Day is sung every year too and genuinely does have three years of propers, so
          &ldquo;we sing it annually&rdquo; is not the test. The test is which lectionary the service runs on.
        </p>

        <h3 className="mt-8 text-xl font-semibold">Organ voluntaries had inherited &ldquo;a cappella&rdquo;</h3>
        <p>
          The voluntaries were sitting inside the Sunday tabs and had picked up the accompaniment value of the choral
          rows around them. For an organ piece that field is not wrong, it is inapplicable, and the fix is to stop
          asking rather than to correct the answer. Their days needed rethinking too: this collection keys its anthems
          to specific Sundays and mostly treats voluntaries as seasonal.
        </p>

        <h3 className="mt-8 text-xl font-semibold">
          A cell reading &ldquo;SSA or SATB&rdquo; has to match both voicing filters
        </h3>
        <p>
          Susan found this one. She asked me for a recommendation for a women&rsquo;s choir and discovered she could not
          search by voicing at all, so voicing became a filter that afternoon and learned to read compound cells the
          same day. She found a regression in it hours later, from a link I had given her, which is how I know she uses
          this site for real work rather than to check on us.
        </p>

        <p className="mt-8">
          None of these are website problems. They are the reason a rebuild like this usually stalls: the person who can
          write the code has to ask the client what a lectionary year is, and the answer arrives in a long email that
          gets half-implemented. It is the same shape whether the vocabulary is lectionary years, application rounds at{' '}
          <Link to="/blog/chroma-festival-case-study" className="text-primary hover:underline">
            a festival
          </Link>
          , or judging categories in{' '}
          <Link to="/blog/sierra-canyon-case-study" className="text-primary hover:underline">
            a school photo competition
          </Link>
          . Somebody has to know what the words mean before the software can be right about them.
        </p>
      </section>

      <Separator />

      {/* Handover */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">It Is Hers to Run</h2>
        <p>
          This is a paid engagement rather than a concept, and it is live on the client&rsquo;s own domain:{' '}
          <a
            href="https://greathostcomposers.org"
            target="_blank"
            rel="noopener"
            className="text-primary hover:underline"
          >
            greathostcomposers.org
          </a>
          , as of August 5, 2026.
        </p>
        <p>
          The domain carried live Google Workspace mail, so the risk in that move was never the website. Moving
          nameservers without recreating the mail records first takes a parish&rsquo;s email down, and the outage is
          invisible until someone mentions they never got a reply. The records were proved equivalent before the cut and
          the mail path was tested after it.
        </p>
        <p>
          Susan adds and edits the works herself, in an admin built for the way church musicians describe music rather
          than the way a generic content system does. She corrects a composer&rsquo;s name and the site republishes,
          with her old page address kept alive so nothing she has already emailed to her contributors breaks. She writes
          her own news entries. The database is a living document again, which is what it was in the spreadsheet, and
          the reason a spreadsheet was hard to give up.
        </p>

        <blockquote className="border-primary my-8 border-l-4 pl-6 text-lg italic">
          <p>
            &ldquo;I&rsquo;m very pleased with how this has turned out. I think once you have those additional things
            editable, once a month will work very well.&rdquo;
          </p>
          <footer className="text-muted-foreground mt-2 text-sm not-italic">
            Susan Jane Matthews, Founding Coordinator, on the evening the domain cut over
          </footer>
        </blockquote>

        <p>
          The second half of that is the part I care about. She is describing a working rhythm, once a month, which only
          holds if she can make her own corrections. If she still has to email me to fix a typo, a retainer is just a
          support queue with a nicer name, and there are pages on that site I have not finished handing over yet.
        </p>
        <p>
          The things a board asks about are all settled in her favor. She owns the domain, the registrar account, and
          the data. The catalogue exports. The site is static files on her own address, so it keeps serving whether or
          not she is still working with me, and moving it elsewhere does not require my cooperation. I would rather earn
          the next month than hold anyone&rsquo;s website hostage to it.
        </p>
        <p>
          One honest caveat about all those new pages: they went up yesterday. Search engines have not had time to do
          anything with them, and page count is an input rather than a result. I will come back and report what actually
          gets indexed and what people search for on the way in.
        </p>
      </section>

      {/* Visual: the homepage pair, held back to break up the tail */}
      <figure className="not-prose article-section">
        <BeforeAfterSlider
          beforeSrc="https://images.fuguelab.app/media/case-studies/great-host-composers/blog/before-home.webp"
          afterSrc="https://images.fuguelab.app/media/case-studies/great-host-composers/blog/after-home.webp"
          beforeAlt="The old Weebly homepage: the tree-of-notes logo on a sky gradient, the wordmark, the Psalm 68 epigraph, and a line of plain text links reading Sacred Music Database, News, Articles"
          afterAlt="The rebuilt homepage: one of Susan Jane Matthews' own bird photographs carrying the same Psalm 68 epigraph, a search field beneath it, and a facing column of composer names"
          beforeLabel="Weebly"
          afterLabel="Rebuilt"
          width={1366}
          height={854}
        />
        <figcaption className="text-muted-foreground mt-3 text-sm">
          The homepage, before and after. The logo, the epigraph and the photography are all hers and all predate us.
          What changed is that the composers are on the front page now, by name, because they are what people came for.
        </figcaption>
      </figure>

      <Separator />

      {/* Lessons */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">If Your Best Work Is in a Spreadsheet</h2>
        <p>
          I have seen the same file at a festival, a choral society and a music school: an alumni directory, a concert
          archive going back forty years, a commissioning history, a repertoire list four staff members maintain in
          parallel. It took years, and it is parked behind a link that says &ldquo;view our database.&rdquo;
        </p>
        <p>
          A spreadsheet answers questions in the order its columns happen to be arranged, and every search engine on
          earth treats it as one blank page. Your users arrive holding a date, a name, or a constraint, and the file
          makes them do the joining. It is worth turning into a website when the data is good, and it is worth doing
          with someone who knows what your columns mean.
        </p>
      </section>

      <Separator />

      {/* CTA */}
      <section className="bg-primary/5 border-primary/20 article-section space-y-6 rounded-lg border p-8 text-center">
        <h3 className="text-2xl font-semibold">Send Me the Link, Not the File</h3>
        <p className="text-muted-foreground mx-auto max-w-2xl">
          Send the URL of the page your file currently sits behind. Everything I diagnosed above, the affiliate
          redirect, the six pages, the fact that none of it was readable, came from the public site, so I do not need
          your data to tell you where you stand. Within a week you get a written read: what is invisible to search, what
          a rebuild would involve, and a cost range. No charge and no obligation, and I do not need the spreadsheet
          itself unless we both decide to go ahead.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
          <m.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <Link to="/contact">
              <Button variant="default" size="lg" className="cursor-pointer">
                Send Me Your Link &rarr;
              </Button>
            </Link>
          </m.div>
          <m.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <a href="https://greathostcomposers.org" target="_blank" rel="noopener">
              <Button variant="outline" size="lg" className="cursor-pointer">
                Visit greathostcomposers.org
              </Button>
            </a>
          </m.div>
        </div>
        <p className="text-muted-foreground text-sm">
          Or write to me directly:{' '}
          <a href="mailto:garrett@fuguelab.com" className="text-primary hover:underline">
            garrett@fuguelab.com
          </a>
        </p>
      </section>

      <Separator />

      {/* Author Bio */}
      <section className="border-border border-t pt-8">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <h2 className="text-lg font-semibold">About the Author</h2>
            <p className="text-muted-foreground mt-2">
              <strong>Garrett John Law</strong> is an organist and choir director at an Episcopal parish, and the
              founder of Fugue Lab, where he builds and runs the digital side for music organizations. He plans music
              against the same lectionary this database indexes, most weeks of the year.
            </p>
            <div className="mt-4">
              <Link to="/about" className="text-primary text-sm hover:underline">
                Learn More about Fugue Lab &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>
    </article>
  )
}
